import React from 'react';
import {
  // Navigation & UI
  HomeIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  BoltIcon,
  SunIcon,
  CloudIcon,
  // Status & Alerts
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  BellIcon,
  // Data & Analytics
  ChartPieIcon,
  PresentationChartLineIcon,
  TableCellsIcon,
  DocumentChartBarIcon,
  // Equipment & Technical
  CpuChipIcon,
  ServerIcon,
  RectangleStackIcon,
  LightBulbIcon,
  // Actions
  PlayIcon,
  PauseIcon,
  StopIcon,
  ArrowPathIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  // Navigation
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  // Export & Download
  ArrowDownTrayIcon,
  DocumentArrowDownIcon,
  PrinterIcon,
  ShareIcon,
  // User & Settings
  UserIcon,
  UsersIcon,
  KeyIcon,
  ShieldCheckIcon,
  // Time & Calendar
  CalendarIcon,
  ClockIcon,
  // Search & Filter
  MagnifyingGlassIcon,
  FunnelIcon,
  AdjustmentsHorizontalIcon,
  // Communication
  WifiIcon,
  SignalIcon,
  GlobeAltIcon,
  // Weather & Environment
  BeakerIcon,
  StarIcon,
  SparklesIcon,
  // Additional specific icons
  ArrowsRightLeftIcon,
  ArrowTrendingUpIcon,
  CursorArrowRaysIcon,
  HeartIcon,
  AcademicCapIcon,
  CircleStackIcon,
  CommandLineIcon,
  CubeTransparentIcon,
} from '@heroicons/react/24/outline';

import {
  // Filled versions for active states
  HomeIcon as HomeIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  BoltIcon as BoltIconSolid,
  SunIcon as SunIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
  ExclamationTriangleIcon as ExclamationTriangleIconSolid,
  XCircleIcon as XCircleIconSolid,
  BellIcon as BellIconSolid,
} from '@heroicons/react/24/solid';

export type IconName = 
  // Navigation & UI
  | 'home' | 'settings' | 'chart-bar' | 'bolt' | 'sun' | 'cloud'
  // Status & Alerts  
  | 'check-circle' | 'warning' | 'x-circle' | 'info' | 'bell'
  // Data & Analytics
  | 'chart-pie' | 'chart-line' | 'table' | 'document-chart'
  // Equipment & Technical
  | 'cpu' | 'circuit' | 'battery' | 'power'
  // Actions
  | 'play' | 'pause' | 'stop' | 'refresh' | 'eye' | 'edit' | 'delete' | 'plus'
  // Navigation
  | 'chevron-left' | 'chevron-right' | 'chevron-up' | 'chevron-down' 
  | 'arrow-left' | 'arrow-right'
  // Export & Download
  | 'download' | 'document-download' | 'print' | 'share'
  // User & Settings
  | 'user' | 'users' | 'key' | 'shield'
  // Time & Calendar
  | 'calendar' | 'clock'
  // Search & Filter
  | 'search' | 'filter' | 'adjustments'
  // Communication
  | 'wifi' | 'signal' | 'globe'
  // Weather & Environment
  | 'beaker' | 'fire' | 'snowflake'
  // Specific Chart Icons
  | 'weather-trends' | 'correlation' | 'wind-analysis' | 'model-comparison'
  | 'error-distribution' | 'accuracy-trend' | 'neural-network' | 'model-health'
  | 'forecast' | 'backtest' | 'roc-curve' | 'pipeline-flow'
  | 'real-time-power' | 'system-health' | 'report-stats';

interface IconProps {
  name: IconName;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'outline' | 'solid';
  className?: string;
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'gray';
}

const iconMap = {
  // Navigation & UI
  home: { outline: HomeIcon, solid: HomeIconSolid },
  settings: { outline: Cog6ToothIcon, solid: Cog6ToothIcon },
  'chart-bar': { outline: ChartBarIcon, solid: ChartBarIconSolid },
  bolt: { outline: BoltIcon, solid: BoltIconSolid },
  sun: { outline: SunIcon, solid: SunIconSolid },
  cloud: { outline: CloudIcon, solid: CloudIcon },
  
  // Status & Alerts
  'check-circle': { outline: CheckCircleIcon, solid: CheckCircleIconSolid },
  warning: { outline: ExclamationTriangleIcon, solid: ExclamationTriangleIconSolid },
  'x-circle': { outline: XCircleIcon, solid: XCircleIconSolid },
  info: { outline: InformationCircleIcon, solid: InformationCircleIcon },
  bell: { outline: BellIcon, solid: BellIconSolid },
  
  // Data & Analytics
  'chart-pie': { outline: ChartPieIcon, solid: ChartPieIcon },
  'chart-line': { outline: PresentationChartLineIcon, solid: PresentationChartLineIcon },
  table: { outline: TableCellsIcon, solid: TableCellsIcon },
  'document-chart': { outline: DocumentChartBarIcon, solid: DocumentChartBarIcon },
  
  // Equipment & Technical
  cpu: { outline: CpuChipIcon, solid: CpuChipIcon },
  circuit: { outline: ServerIcon, solid: ServerIcon },
  battery: { outline: RectangleStackIcon, solid: RectangleStackIcon },
  power: { outline: LightBulbIcon, solid: LightBulbIcon },
  
  // Actions
  play: { outline: PlayIcon, solid: PlayIcon },
  pause: { outline: PauseIcon, solid: PauseIcon },
  stop: { outline: StopIcon, solid: StopIcon },
  refresh: { outline: ArrowPathIcon, solid: ArrowPathIcon },
  eye: { outline: EyeIcon, solid: EyeIcon },
  edit: { outline: PencilIcon, solid: PencilIcon },
  delete: { outline: TrashIcon, solid: TrashIcon },
  plus: { outline: PlusIcon, solid: PlusIcon },
  
  // Navigation
  'chevron-left': { outline: ChevronLeftIcon, solid: ChevronLeftIcon },
  'chevron-right': { outline: ChevronRightIcon, solid: ChevronRightIcon },
  'chevron-up': { outline: ChevronUpIcon, solid: ChevronUpIcon },
  'chevron-down': { outline: ChevronDownIcon, solid: ChevronDownIcon },
  'arrow-left': { outline: ArrowLeftIcon, solid: ArrowLeftIcon },
  'arrow-right': { outline: ArrowRightIcon, solid: ArrowRightIcon },
  
  // Export & Download
  download: { outline: ArrowDownTrayIcon, solid: ArrowDownTrayIcon },
  'document-download': { outline: DocumentArrowDownIcon, solid: DocumentArrowDownIcon },
  print: { outline: PrinterIcon, solid: PrinterIcon },
  share: { outline: ShareIcon, solid: ShareIcon },
  
  // User & Settings
  user: { outline: UserIcon, solid: UserIcon },
  users: { outline: UsersIcon, solid: UsersIcon },
  key: { outline: KeyIcon, solid: KeyIcon },
  shield: { outline: ShieldCheckIcon, solid: ShieldCheckIcon },
  
  // Time & Calendar
  calendar: { outline: CalendarIcon, solid: CalendarIcon },
  clock: { outline: ClockIcon, solid: ClockIcon },
  
  // Search & Filter
  search: { outline: MagnifyingGlassIcon, solid: MagnifyingGlassIcon },
  filter: { outline: FunnelIcon, solid: FunnelIcon },
  adjustments: { outline: AdjustmentsHorizontalIcon, solid: AdjustmentsHorizontalIcon },
  
  // Communication
  wifi: { outline: WifiIcon, solid: WifiIcon },
  signal: { outline: SignalIcon, solid: SignalIcon },
  globe: { outline: GlobeAltIcon, solid: GlobeAltIcon },
  
  // Weather & Environment
  beaker: { outline: BeakerIcon, solid: BeakerIcon },
  fire: { outline: StarIcon, solid: StarIcon },
  snowflake: { outline: SparklesIcon, solid: SparklesIcon },
  
  // Specific Chart Icons
  'weather-trends': { outline: CloudIcon, solid: CloudIcon },
  'correlation': { outline: ArrowsRightLeftIcon, solid: ArrowsRightLeftIcon },
  'wind-analysis': { outline: CursorArrowRaysIcon, solid: CursorArrowRaysIcon },
  'model-comparison': { outline: CubeTransparentIcon, solid: CubeTransparentIcon },
  'error-distribution': { outline: ChartBarIcon, solid: ChartBarIcon },
  'accuracy-trend': { outline: ArrowTrendingUpIcon, solid: ArrowTrendingUpIcon },
  'neural-network': { outline: AcademicCapIcon, solid: AcademicCapIcon },
  'model-health': { outline: HeartIcon, solid: HeartIcon },
  'forecast': { outline: PresentationChartLineIcon, solid: PresentationChartLineIcon },
  'backtest': { outline: CircleStackIcon, solid: CircleStackIcon },
  'roc-curve': { outline: ChartPieIcon, solid: ChartPieIcon },
  'pipeline-flow': { outline: CommandLineIcon, solid: CommandLineIcon },
  'real-time-power': { outline: BoltIcon, solid: BoltIcon },
  'system-health': { outline: CheckCircleIcon, solid: CheckCircleIcon },
  'report-stats': { outline: DocumentChartBarIcon, solid: DocumentChartBarIcon },
};

const sizeClasses = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4', 
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
  xl: 'w-8 h-8'
};

const colorClasses = {
  primary: 'text-blue-600 dark:text-blue-400',
  success: 'text-green-600 dark:text-green-400',
  warning: 'text-yellow-600 dark:text-yellow-400',
  danger: 'text-red-600 dark:text-red-400',
  info: 'text-purple-600 dark:text-purple-400',
  gray: 'text-gray-600 dark:text-gray-400'
};

export const Icon: React.FC<IconProps> = ({ 
  name, 
  size = 'md', 
  variant = 'outline',
  className = '',
  color = 'gray'
}) => {
  const IconComponent = iconMap[name]?.[variant];
  
  if (!IconComponent) {
    console.warn(`Icon "${name}" with variant "${variant}" not found`);
    return null;
  }

  const colorMap = {
    primary: '#3b82f6',
    success: '#10b981', 
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#8b5cf6',
    gray: '#6b7280'
  };

  const classes = `${sizeClasses[size]} ${className}`.trim();

  return (
    <IconComponent 
      className={classes} 
      style={{ 
        color: colorMap[color],
        fill: 'currentColor',
        stroke: 'currentColor'
      }} 
    />
  );
};

export default Icon;